/**
 * OrgDocuments — the Documents section on an organization's directory
 * page: a flyer, a rate sheet, a referral form, attached to the card so
 * the next care manager who opens the organization finds them.
 *
 * It sits on the org page's Section register beside Good to know and
 * Services & specialties, and it speaks the card-attachments client
 * pattern (AttachmentsBlock on a Workspace card): one mutation drives
 * the three-step handshake, the client refuses a bad type or an
 * oversized file BEFORE any byte leaves the browser, and the row's lead
 * affordance is chosen by the STORED content type — pdf/png/jpeg get
 * Open (an inline grant in a new tab), docx gets Download. The popup
 * dance (open a blank tab synchronously on the click, sever its opener,
 * point it at the grant when it lands) is the shipped house-safe
 * pattern from documentGrants.ts.
 *
 * WHO. Attaching is for anyone who can suggest an edit to the directory
 * (the section's `canAttach`); the list reads for everyone who can open
 * the organization. Removing is the uploader's own, or a direct-write
 * seat's (`canRemoveAny`) — the server enforces both; the props only
 * decide what is drawn.
 *
 * WHAT DOES NOT BELONG HERE. Client documents (POLSTs, POAs, insurance
 * cards, consents) live in the clinical store on the client record.
 * This section is for the organization's own paper.
 *
 * DELETE IS PERMANENT. No archive shelf, no undo; the confirm carries
 * the weight and its copy must never imply the file can come back.
 */

import { useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  isAttachmentPreviewable,
  providerAttachmentsApi,
  providerAttachmentsKey,
  resolveAttachmentContentType,
  type ProviderAttachmentWire,
  type ProviderAttachmentsListResponse,
} from '@/lib/providerAttachments';
import { openBlankViewerTab, pointViewerAt, saveGrantToDisk } from '@/lib/documentGrants';
import { formatRelative } from '@/lib/format';
import { Section } from '@/components/peace-hub-port/ResourceDetail';
import { Button } from '@/components/primitives/Button';
import { ConfirmDialog } from '@/components/workspace/ConfirmDialog';
import { TextAction, DestructiveTextAction } from '@/components/workspace/TextAction';
import { contactSentence } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

/** The upload's retry line; `{contact_system}` is filled in at render. */
const UPLOAD_FAILED_COPY = "That file didn't upload. Add it again, and if it fails again, tell {contact_system}.";

/** The open or download failure line (house failure copy). */
const GRANT_FAILED_COPY =
  "That file didn't open. Try again, and if it happens again, tell {contact_system}.";

/** What the picker will not take, said in file terms and not mime terms. */
const TYPE_REJECTED_COPY =
  'That file type is not one the directory takes. A PDF, Word document, or image will go right in.';

const SIZE_REJECTED_COPY = 'That file is over the 25 MB limit for a directory document.';

/** Quiet size label (a fact for the meta line). */
export function formatDocumentSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export interface OrgDocumentsProps {
  tenantSlug: string;
  providerId: string;
  /** suggest — may attach. The server re-checks. */
  canAttach: boolean;
  /** direct_write — may remove anyone's file. The server re-checks. */
  canRemoveAny: boolean;
  /** Decides whether a row's Delete shows (own files). */
  currentUserId: string | null;
}

export function OrgDocuments({
  tenantSlug,
  providerId,
  canAttach,
  canRemoveAny,
  currentUserId,
}: OrgDocumentsProps): ReactNode {
  const contact = useSupportContact({ enabled: false });
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /** The last attempted file, retained so retry is one click. */
  const [lastAttempt, setLastAttempt] = useState<File | null>(null);
  /** A client-side rejection, surfaced BEFORE any byte leaves the browser. */
  const [pickError, setPickError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<ProviderAttachmentWire | null>(null);

  const queryKey = providerAttachmentsKey(tenantSlug, providerId);
  const listQuery = useQuery<ProviderAttachmentsListResponse>({
    queryKey,
    queryFn: ({ signal }) => providerAttachmentsApi.list(tenantSlug, providerId, signal),
  });
  const rows = listQuery.data?.rows ?? [];

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey });
  }

  const attachMut = useMutation({
    mutationFn: (file: File) => providerAttachmentsApi.attach(tenantSlug, providerId, file),
    onSuccess: () => {
      setLastAttempt(null);
      invalidate();
    },
  });

  const removeMut = useMutation({
    mutationFn: (attachmentId: string) => providerAttachmentsApi.remove(tenantSlug, attachmentId),
    onSuccess: () => {
      setRemoveTarget(null);
      invalidate();
    },
  });

  const downloadMut = useMutation({
    mutationFn: (attachmentId: string) =>
      providerAttachmentsApi.downloadGrant(tenantSlug, attachmentId, 'attachment'),
    onSuccess: (grant) => {
      saveGrantToDisk(grant.download_url);
    },
  });

  const openMut = useMutation({
    mutationFn: (vars: { attachmentId: string; viewer: Window | null }) =>
      providerAttachmentsApi.downloadGrant(tenantSlug, vars.attachmentId, 'inline'),
    onSuccess: (grant, vars) => {
      pointViewerAt(vars.viewer, grant.download_url);
    },
    onError: (_err, vars) => {
      // Never strand a blank tab behind a failed grant.
      vars.viewer?.close();
    },
  });

  function openDocument(attachmentId: string): void {
    // Synchronous open on the gesture, pointed at the grant when it lands.
    openMut.mutate({ attachmentId, viewer: openBlankViewerTab() });
  }

  /** Client-side policy first: the user hears no before anything uploads. */
  function startAttach(file: File): void {
    setPickError(null);
    if (resolveAttachmentContentType(file) === null) {
      setPickError(TYPE_REJECTED_COPY);
      return;
    }
    if (file.size <= 0 || file.size > ATTACHMENT_MAX_BYTES) {
      setPickError(SIZE_REJECTED_COPY);
      return;
    }
    setLastAttempt(file);
    attachMut.mutate(file);
  }

  function onPick(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) startAttach(file);
  }

  function canRemove(row: ProviderAttachmentWire): boolean {
    return canRemoveAny || (currentUserId !== null && row.uploaded_by_user_id === currentUserId);
  }

  const showAttachError = !attachMut.isPending && (attachMut.isError || pickError !== null);

  return (
    <div data-testid="org-documents">
      <Section title="Documents">
        {canAttach && (
          <div className="mb-3">
            <input
              ref={fileInputRef}
              data-testid="org-documents-input"
              type="file"
              accept={ATTACHMENT_ACCEPT}
              className="sr-only"
              onChange={onPick}
            />
            {attachMut.isPending ? (
              <p data-testid="org-documents-uploading" className="text-xs text-ink" role="status">
                Attaching…
              </p>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                data-testid="org-documents-attach"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip size={14} aria-hidden="true" />
                Attach a document
              </Button>
            )}
            {showAttachError && (
              <p role="alert" data-testid="org-documents-error" className="mt-2 text-label text-pepper-deep">
                {pickError ?? contactSentence(UPLOAD_FAILED_COPY, contact)}{' '}
                {pickError === null && lastAttempt && (
                  <TextAction data-testid="org-documents-retry" onClick={() => attachMut.mutate(lastAttempt)}>
                    Try again
                  </TextAction>
                )}
              </p>
            )}
          </div>
        )}

        {listQuery.isError ? (
          <p role="alert" data-testid="org-documents-list-error" className="text-xs text-pepper-deep">
            The documents for this organization did not load.{' '}
            <TextAction onClick={() => void listQuery.refetch()}>Try again</TextAction>
          </p>
        ) : listQuery.isPending ? (
          <p className="text-sm text-ink-soft" role="status">
            Loading…
          </p>
        ) : rows.length === 0 ? (
          <p data-testid="org-documents-empty" className="text-sm text-ink-soft">
            Add a flyer, a rate sheet or a referral form for this organization.
          </p>
        ) : (
          <ul className="divide-y divide-sage-800/[6%]" data-testid="org-documents-list">
            {rows.map((row) => {
              const previewable = isAttachmentPreviewable(row.content_type);
              return (
                <li key={row.id} data-testid={`org-document-row-${row.id}`} className="flex flex-col gap-0.5 py-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <span className="min-w-0 flex-1 break-all text-title-sm font-medium text-ink">
                      {row.display_name}
                    </span>
                    {previewable ? (
                      <TextAction
                        data-testid={`org-document-open-${row.id}`}
                        disabled={openMut.isPending && openMut.variables?.attachmentId === row.id}
                        onClick={() => openDocument(row.id)}
                      >
                        Open
                      </TextAction>
                    ) : (
                      <TextAction
                        data-testid={`org-document-download-${row.id}`}
                        disabled={downloadMut.isPending && downloadMut.variables === row.id}
                        onClick={() => downloadMut.mutate(row.id)}
                      >
                        Download
                      </TextAction>
                    )}
                    {canRemove(row) && (
                      <DestructiveTextAction
                        data-testid={`org-document-delete-${row.id}`}
                        onClick={() => setRemoveTarget(row)}
                      >
                        Delete
                      </DestructiveTextAction>
                    )}
                  </div>
                  <p className="text-label text-sage-400">
                    {row.uploaded_by_display_name ? `${row.uploaded_by_display_name} · ` : ''}
                    {formatRelative(row.created_at)} · {formatDocumentSize(row.size_bytes)}
                  </p>
                </li>
              );
            })}
          </ul>
        )}

        {(downloadMut.isError || openMut.isError) && (
          <p role="alert" data-testid="org-documents-grant-error" className="mt-2 text-label text-pepper-deep">
            {contactSentence(GRANT_FAILED_COPY, contact)}
          </p>
        )}
      </Section>

      {removeTarget !== null && (
        <ConfirmDialog
          title="Delete this document?"
          body={`"${removeTarget.display_name}" leaves this organization for everyone, and the file itself is deleted. This can't be undone.`}
          confirmLabel="Delete"
          destructive
          onCancel={() => setRemoveTarget(null)}
          onConfirm={() => removeMut.mutate(removeTarget.id)}
        />
      )}
    </div>
  );
}
